import {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";

import Loading from "@/components/Loading";
import Page from "@/components/Page";
import AttachmentsContainer, {
	getRelativePath,
} from "@/components/AttachmentsContainer";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import Rating from "@/components/Rating";
import ViewField from "@/components/Input/ViewField";
import VLFCard from "@/components/Card";
import VLFModal from "@/components/Modal";
import VLFTextField from "@/components/Input/TextField";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import {AttachmentAPI} from "@/api/attachment";
import {ExerciseAPI} from "@/api/exercise";
import pathUrl from "@/utils/path";

import {ERROR_OPERATION, SUCCESSFUL_OPERATION} from "@/utils/constants";
import {getAttachment} from "@/utils/functions";

function ExercisePage(props) {
	const {handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();

	const [loading, setLoading] = useState(false);
	const [exerciseData, setExerciseData] = useState({});

	const [attachments, setAttachments] = useState([]);

	const [entityToDelete, setEntityToDelete] = useState({});
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);

	const getExercise = async () => {
		setLoading(true);
		try {
			const response = await ExerciseAPI.getById(id);
			setExerciseData(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const getExerciseAttachment = async () => {
		try {
			const response = await AttachmentAPI.list("exercise", id);
			setAttachments(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	useEffect(() => {
		getExercise();
		getExerciseAttachment();
	}, []);

	const deleteExercise = async () => {
		setLoading(true);
		try {
			await ExerciseAPI.delete(exerciseData?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/exercises");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	const deleteAttachment = async () => {
		setLoading(true);
		try {
			await AttachmentAPI.delete(entityToDelete?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleConfirm(false);
			getExerciseAttachment();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleDownload = async (id) => {
		setLoading(true);
		try {
			await getAttachment(id, attachments);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const mainConfiguration = [
		{
			component: (
				<div className="d-flex flex-column h-100">
					<div className="view-field-container mb-2">
						<div className="label"></div>
						<div
							className="value big"
							style={{
								backgroundColor: "#ebf1fd",
								padding: "2px 6px",
								borderRadius: "10px",
							}}
						>
							{exerciseData?.name || "---"}
						</div>
					</div>

					<div className="d-flex flex-column h-100">
						<div className="view-field-container mb-2">
							<div className="label">{"Tipologia"}</div>
							<div className="value">{exerciseData?.exerciseType || "---"}</div>
						</div>

						<div className="view-field-container mb-2">
							<div className="label">{"Tempo di svolgimento"}</div>
							<div className="value">
								{exerciseData?.duration ? exerciseData?.duration + "'" : "---"}
							</div>
						</div>

						<div className="view-field-container">
							<div className="label">{"Obiettivi"}</div>
							<div className="value">{exerciseData?.objectives || "---"}</div>
						</div>
						<div className="view-field-container">
							<div className="label">{"Obiettivi secondari"}</div>
							<div className="value">
								{exerciseData?.secondaryObjectives || "---"}
							</div>
						</div>
					</div>
				</div>
			),
			label: "",
			size: "col-12 col-lg-6",
			className: "bordered bottom",
		},
		{
			value: (
				<img
					src={
						exerciseData?.imageUrl ||
						getRelativePath(attachments?.[0]?.filePath || "", pathUrl)
					}
					alt=""
				/>
			),
			label: "",
			size: "col-12 col-lg-6",
			className:
				"bordered bottom d-flex align-items-center justify-content-center",
		},
		{
			value: (
				<VLFTextField
					value={
						exerciseData?.materials?.length
							? exerciseData.materials
							: "Nessun materiale inserito"
					}
					size="small"
					multiline
					minRows={1}
					readOnly
				/>
			),
			label: "Materiale",
			size: "col-12 col-lg-6",
			className: "value-highlighted",
		},
		{
			value: (
				<VLFTextField
					value={
						exerciseData?.preparation?.length
							? exerciseData.preparation
							: "Nessuna preparazione inserita"
					}
					size="small"
					multiline
					minRows={1}
					readOnly
				/>
			),
			label: "Preparazione",
			size: "col-12 col-lg-6",
			className: "value-highlighted",
		},
		{
			value: (
				<VLFTextField
					value={
						exerciseData?.summary?.length
							? exerciseData.summary
							: "Nessun somario inserito"
					}
					size="small"
					multiline
					minRows={1}
					readOnly
				/>
			),
			label: "Sommario",
			size: "col-12 col-lg-6",
		},
		{
			value: (
				<VLFTextField
					value={
						exerciseData?.organization?.length
							? exerciseData.organization
							: "Nessuna organizzazione inserita"
					}
					size="small"
					multiline
					minRows={1}
					readOnly
				/>
			),
			label: "Organizzazione",
			size: "col-12 col-lg-6",
		},
		{
			value: (
				<VLFTextField
					value={
						exerciseData?.description?.length
							? exerciseData.description
							: "Nessuna descrizione inserita"
					}
					size="small"
					multiline
					minRows={1}
					readOnly
				/>
			),
			label: "Descrizione",
			size: "col-12",
		},
		{
			value: (
				<VLFTextField
					value={
						exerciseData?.rules?.length
							? exerciseData.rules
							: "Nessuna regola inserita"
					}
					size="small"
					multiline
					minRows={1}
					readOnly
				/>
			),
			label: "Regole",
			size: "col-12",
		},
		{
			value: (
				<VLFTextField
					value={
						exerciseData?.variants?.length
							? exerciseData.variants
							: "Nessuna variante inserita"
					}
					size="small"
					multiline
					minRows={1}
					readOnly
				/>
			),
			label: "Varianti",
			size: "col-12",
		},
		{
			value: (
				<VLFTextField
					value={
						exerciseData?.coachingThemes?.length
							? exerciseData.coachingThemes
							: "Nessun tema inserito"
					}
					size="small"
					multiline
					minRows={1}
					readOnly
				/>
			),
			label: "Temi per l'allenatore",
			size: "col-12",
		},
		{
			value: <Rating selectedRating={exerciseData.difficulty} max={5} />,
			label: "Difficoltà",
			size: "col-12 col-lg-6",
		},
		{
			value: (
				<div className="categories-list">
					{exerciseData?.tags?.split(",")?.map((t, k) => {
						return (
							<div key={k} className="categories-element">
								{t}
							</div>
						);
					})}
				</div>
			),
			label: "Tag",
			size: "col-12 col-lg-6",
		},
		{
			value: exerciseData?.basicActivities ? (
				<div className="check-container">
					<img src={require("@/assets/images/check.png")} alt="" />
				</div>
			) : (
				""
			),
			label: "Attività base",
			size: "col-12 col-lg-6",
		},
		{
			value: exerciseData?.goalkeepers ? (
				<div className="check-container">
					<img src={require("@/assets/images/check.png")} alt="" />
				</div>
			) : (
				""
			),
			label: "Portieri",
			size: "col-12 col-lg-6",
		},
	];

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate("/exercises"),
			color: "info",
		},
		{
			icon: <EditIcon />,
			title: "Modifica",
			onClick: () => navigate("/exercises/" + exerciseData?.id + "/edit"),
		},
		{
			icon: <DeleteIcon />,
			title: "Elimina",
			onClick: () => {
				setEntityToDelete({type: "exercise", id});
				setOpenHandleConfirm(true);
			},
			color: "error",
		},
	];

	const breadcrumbsElements = [
		{label: "Esercizi", path: "/exercises"},
		{label: "/"},
	];

	return (
		<Page className="page-container">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={exerciseData ? exerciseData.name : ""}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="row no-margin">
				<div className="col-sm-12 padding-small">
					<VLFCard
						title="Informazioni principali"
						content={
							<div className="row">
								<div className="col-sm-12">
									<ViewField configuration={mainConfiguration} />
								</div>
							</div>
						}
						openable={false}
					/>
				</div>
			</div>
			<div className="row no-margin mt-2">
				<div className="col-sm-12 padding-small">
					<VLFCard
						title="Allegati"
						content={
							<AttachmentsContainer
								entityId={id}
								entityType="exercise"
								attachments={attachments}
								attachmentClassName="large"
								handleAdd={attachments?.length === 0 && getExerciseAttachment}
								handleDownload={handleDownload}
								handleDelete={(id) => {
									setEntityToDelete({type: "attachment", id});
									setOpenHandleConfirm(true);
								}}
								handleNotification={handleNotification}
							/>
						}
					/>
					<VLFModal
						open={openHandleConfirm}
						title="Eliminazione"
						content={
							<div className="mt-2">
								Sei sicuro di voler eliminare questo elemento?
							</div>
						}
						confirm={() => {
							switch (entityToDelete?.type) {
								case "exercise":
									return deleteExercise();
								case "attachment":
									return deleteAttachment();
								default:
									return;
							}
						}}
						close={() => setOpenHandleConfirm(false)}
						confirmLabel="Conferma"
					/>
				</div>
			</div>
		</Page>
	);
}

export default ExercisePage;
